import React from "react";
import {
  TextField,
  Button,
  MenuItem,
  Grid,
  Typography,
  Box,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormControl,
  FormLabel
} from "@mui/material";
import { sharedStyles } from '../styles/sharedStyles';
import { getEnums } from '../../utils/enumConfig';

const debitOrderDates = [
  { value: '1', label: '1ST' },
  { value: '5', label: '5TH' },
  { value: '15', label: '15TH' },
  { value: '25', label: '25TH' }
];

const Step5BankingDetails = ({ formData, setFormData, errors = {}, setErrors, handleNext, handleBack, isCurrentStep }) => {
  // Get dynamic enums based on selected plan
  const enums = getEnums(formData.recommendedMedicalAidPlan);
  const { accountTypes, bankOptions } = enums;
  
  // Helper function to get bank API data
  const getBankApiData = (selected) => {
    const found = bankOptions.find(b => b.value === selected || b.api === selected);
    return found ? { bankname: found.api, branchcode: found.code, branchname: found.branchname } : { bankname: '', branchcode: '', branchname: '' };
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Handle bank dropdown for contributions
    if (name === 'bankName') {
      const bankData = getBankApiData(value);
      setFormData({
        ...formData,
        bankName: value,
        bankApiName: bankData.bankname,
        branchCode: bankData.branchcode,
        branchName: bankData.branchname
      });
      setErrors({ ...errors, bankName: undefined });
      return;
    }
    // Handle bank dropdown for refunds
    if (name === 'refundBankName') {
      const bankData = getBankApiData(value);
      setFormData({
        ...formData,
        refundBankName: value,
        refundBankApiName: bankData.bankname,
        refundBranchCode: bankData.branchcode,
        refundBranchName: bankData.branchname
      });
      setErrors({ ...errors, refundBankName: undefined });
      return;
    }
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    setErrors({ ...errors, [name]: undefined });
  };

  const validate = () => true;

  const onNext = (e) => {
    e.preventDefault();
    // Prepare API output structure
    const banks_contributions = [{
      thirdpartycheck: formData.isThirdPartyAccount ? 'third_party' : null,
      bankname: formData.bankApiName || '',
      otherbankname: null,
      accholderid: formData.accountHolderId || '',
      accholder: formData.accountHolder || '',
      accholdersurname: null,
      accnum: formData.accountNumber || '',
      acctype: formData.accountType || '',
      debitdate: formData.debitOrderDate || '',
      branchcode: formData.branchCode || '',
      branchname: formData.branchName || '',
      verification: null,
      idvalidation: null,
      consentupload: null,
      company_bank: null
    }];
    let banks_refunds = [];
    if (formData.refundsSameAsContribution === 'different') {
      banks_refunds = [{
        thirdpartycheck: formData.isThirdPartyAccount ? 'third_party' : null,
        sameacccheck: 'different_accounts',
        bankname: formData.refundBankApiName || '',
        otherbankname: null,
        accholderid: formData.refundAccountHolderId || '',
        accholder: formData.refundAccountHolder || '',
        accholdersurname: null,
        accnum: formData.refundAccountNumber || '',
        acctype: formData.refundAccountType || '',
        debitdate: formData.debitOrderDate || '',
        branchcode: formData.refundBranchCode || '',
        branchname: formData.refundBranchName || '',
        verification: null,
        idvalidation: null,
        consentupload: null
      }];
    } else {
      banks_refunds = [{
        thirdpartycheck: formData.isThirdPartyAccount ? 'third_party' : null,
        sameacccheck: 'same_accounts',
        bankname: formData.bankApiName || '',
        otherbankname: null,
        accholderid: formData.accountHolderId || '',
        accholder: formData.accountHolder || '',
        accholdersurname: null,
        accnum: formData.accountNumber || '',
        acctype: formData.accountType || '',
        debitdate: formData.debitOrderDate || '',
        branchcode: formData.branchCode || '',
        branchname: formData.branchName || '',
        verification: null,
        idvalidation: null,
        consentupload: null
      }];
    }
    setFormData({ ...formData, banks_contributions, banks_refunds });
    if (validate()) handleNext();
  };

  return (
    <Box component="form" onSubmit={onNext} autoComplete="off" sx={{ ...sharedStyles.formContainer, pb: '80px' }}>
      <Typography variant="h5" sx={sharedStyles.stepTitle}>
        Banking Details
      </Typography>
      <Paper sx={sharedStyles.card}>
        <Typography sx={{ mb: 1, fontWeight: 500 }}>
          Bank details to use for recovery of Monthly Subscriptions
        </Typography>
        <Typography sx={{ mb: 2 }}>
          Please note: Subscriptions are due on the first (1st) working day of each calendar month in which you are enrolled as a member.
        </Typography>
        <Typography sx={{ mb: 2 }}>
          I hereby authorise Bonitas to recover the applicable monthly subscriptions payable by me to Bonitas by debit order from my bank account monthly on the first workday of each month as from the date of enrolment. I further authorise Bonitas to increase or decrease the subscriptions, should it be necessary, and recover the amended amount, or any subscriptions in arrears, from my bank account.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!formData.isThirdPartyAccount}
              onChange={handleChange}
              name="isThirdPartyAccount"
              color="primary"
            />
          }
          label="THIS IS A THIRD PARTY BANK ACCOUNT"
          sx={{ mb: 3 }}
        />
        <Paper sx={{ ...sharedStyles.card, background: '#393636', color: '#fff', mb: 3, mt: 2 }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 0 }}>
            Contributions
          </Typography>
        </Paper>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Bank Name"
              name="bankName"
              value={formData.bankName || ''}
              onChange={handleChange}
              fullWidth
              {...sharedStyles.inputProps}
              error={!!errors.bankName}
              helperText={errors.bankName}
            >
              {bankOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Branch Name"
              name="branchName"
              value={formData.branchName || ''}
              onChange={handleChange}
              fullWidth
              {...sharedStyles.inputProps}
              error={!!errors.branchName}
              helperText={errors.branchName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Branch Code"
              name="branchCode"
              value={formData.branchCode || ''}
              InputProps={{ readOnly: true }}
              fullWidth
              {...sharedStyles.inputProps}
              error={!!errors.branchCode}
              helperText={errors.branchCode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Account Holder"
              name="accountHolder"
              value={formData.accountHolder || ''}
              onChange={handleChange}
              fullWidth
              {...sharedStyles.inputProps}
              error={!!errors.accountHolder}
              helperText={errors.accountHolder}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Account Number"
              name="accountNumber"
              value={formData.accountNumber || ''}
              onChange={handleChange}
              fullWidth
              {...sharedStyles.inputProps}
              error={!!errors.accountNumber}
              helperText={errors.accountNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Account Type"
              name="accountType"
              value={formData.accountType || ''}
              onChange={handleChange}
              fullWidth
              {...sharedStyles.inputProps}
              error={!!errors.accountType}
              helperText={errors.accountType}
            >
              {accountTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Box sx={{ mt: 3 }}>
          <RadioGroup
            name="refundsSameAsContribution"
            value={formData.refundsSameAsContribution || ''}
            onChange={handleChange}
          >
            <FormControlLabel value="same" control={<Radio color="primary" />} label="REFUNDS IS THE SAME AS THE CONTRIBUTION ACCOUNTS." />
            <FormControlLabel value="different" control={<Radio color="primary" />} label="REFUNDS IS DIFFERENT TO THE CONTRIBUTION ACCOUNTS." />
          </RadioGroup>
        </Box>
        <Box sx={{ mt: 3 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>Debit Order Date</FormLabel>
            <RadioGroup
              row
              name="debitOrderDate"
              value={formData.debitOrderDate || ''}
              onChange={handleChange}
            >
              {debitOrderDates.map((option) => (
                <FormControlLabel key={option.value} value={option.value} control={<Radio color="primary" />} label={option.label} />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
        {/* Refunds Section */}
        {formData.refundsSameAsContribution === 'different' && (
          <>
            <Paper sx={{ ...sharedStyles.card, background: '#393636', color: '#fff', mb: 3, mt: 4 }}>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 0 }}>
                Refunds
              </Typography>
            </Paper>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Bank Name (Refunds)"
                  name="refundBankName"
                  value={formData.refundBankName || ''}
                  onChange={handleChange}
                  fullWidth
                  {...sharedStyles.inputProps}
                  error={!!errors.refundBankName}
                  helperText={errors.refundBankName}
                >
                  {bankOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Branch Name (Refunds)"
                  name="refundBranchName"
                  value={formData.refundBranchName || ''}
                  onChange={handleChange}
                  fullWidth
                  {...sharedStyles.inputProps}
                  error={!!errors.refundBranchName}
                  helperText={errors.refundBranchName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Branch Code (Refunds)"
                  name="refundBranchCode"
                  value={formData.refundBranchCode || ''}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  {...sharedStyles.inputProps}
                  error={!!errors.refundBranchCode}
                  helperText={errors.refundBranchCode}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Account Holder (Refunds)"
                  name="refundAccountHolder"
                  value={formData.refundAccountHolder || ''}
                  onChange={handleChange}
                  fullWidth
                  {...sharedStyles.inputProps}
                  error={!!errors.refundAccountHolder}
                  helperText={errors.refundAccountHolder}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Account Number (Refunds)"
                  name="refundAccountNumber"
                  value={formData.refundAccountNumber || ''}
                  onChange={handleChange}
                  fullWidth
                  {...sharedStyles.inputProps}
                  error={!!errors.refundAccountNumber}
                  helperText={errors.refundAccountNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Account Type (Refunds)"
                  name="refundAccountType"
                  value={formData.refundAccountType || ''}
                  onChange={handleChange}
                  fullWidth
                  {...sharedStyles.inputProps}
                  error={!!errors.refundAccountType}
                  helperText={errors.refundAccountType}
                >
                  {accountTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
      <Box sx={{
        position: 'fixed',
        left: '50%',
        bottom: 0,
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: 600,
        background: '#fff',
        zIndex: 1300,
        display: 'flex',
        justifyContent: 'center',
        gap: 2,
        py: 2,
        borderTop: '1px solid #eee',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
      }}>
        {handleBack && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBack}
            sx={sharedStyles.secondaryButton}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          sx={sharedStyles.primaryButton}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default Step5BankingDetails; 